import EditIcon from "@mui/icons-material/Edit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import { Box, Button, Chip, Tooltip } from "@mui/material"
import { useConfirm } from "material-ui-confirm"
import React from "react"
import { useDispatch } from "react-redux"

import { updateShiftPurposeStatus } from "../../../../../store/gat-admin-slice"

interface GridColumn {
	field: string
	type: string
	minWidth?: number
	width?: number
	flex?: number
	align?: "left" | "center" | "right"
	headerName?: string
	renderHeader?: () => React.ReactNode
	renderCell?: (params: { row: any }) => React.ReactNode
	valueGetter?: (params: { row: any }) => any
	sortable?: boolean
	cellClassName?: string
	headerClassName?: string
}

const useShiftPurposeColumns = (handleOpenEdit: (row: any) => void) => {
	const confirm = useConfirm()
	const dispatch = useDispatch()

	const handleDeactivate = (id: number, name: string) => {
		confirm({
			title: "Deactivate Shift Purpose",
			content: `Deactivating "${name}" will remove it from future shift postings. Past shifts that used this purpose will remain unchanged.`,
			cancellationText: "Cancel",
			confirmationText: (
				<Button variant='contained' color='error'>
					Deactivate
				</Button>
			),
		})
			.then(() =>
				dispatch(updateShiftPurposeStatus({ id, isActive: false }))
			)
			.catch(() => undefined)
	}

	const handleActivate = (id: number, name: string) => {
		confirm({
			title: "Activate Shift Purpose",
			content: `Activating "${name}" will make it available to select when posting future shifts.`,
			cancellationText: "Cancel",
			confirmationText: (
				<Button variant='contained' color='primary'>
					Activate
				</Button>
			),
		})
			.then(() =>
				dispatch(updateShiftPurposeStatus({ id, isActive: true }))
			)
			.catch(() => undefined)
	}

	const columns: GridColumn[] = [
		{
			field: "name",
			type: "string",
			minWidth: 200,
			flex: 1,
			headerName: "Shift Purpose",
			renderHeader: () => (
				<span style={{ padding: "0px 4px", fontWeight: "bold" }}>
					Shift Purpose
				</span>
			),
			renderCell: (params) => (
				<Box
					sx={{
						padding: "0px 4px",
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "14px",
						color: "rgba(0,0,0,0.87)",
					}}
				>
					{params.row.name}
				</Box>
			),
			valueGetter: (params) => params.row.name,
		},
		{
			field: "detailField",
			type: "string",
			minWidth: 220,
			flex: 1,
			headerName: "Required Details",
			sortable: false,
			renderHeader: () => (
				<span style={{ padding: "0px 4px", fontWeight: "bold" }}>
					Required Details
				</span>
			),
			renderCell: (params) =>
				params.row.detailField ? (
					<Box
						sx={{
							padding: "0px 4px",
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "14px",
							color: "rgba(0,0,0,0.87)",
						}}
					>
						{params.row.detailField.label}
					</Box>
				) : (
					<Box
						sx={{
							padding: "0px 4px",
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "14px",
							color: "rgba(0,0,0,0.6)",
						}}
					>
						{"\u2014"}
					</Box>
				),
			valueGetter: (params) => params.row.detailField?.label ?? "",
		},
		{
			field: "isActive",
			type: "boolean",
			minWidth: 140,
			flex: 1,
			headerName: "Status",
			renderHeader: () => (
				<span style={{ padding: "0px 4px", fontWeight: "bold" }}>
					Status
				</span>
			),
			renderCell: (params) => (
				<Chip
					label={params.row.isActive ? "Active" : "Inactive"}
					size='small'
					color={params.row.isActive ? "success" : "default"}
					variant={params.row.isActive ? "filled" : "outlined"}
				/>
			),
			valueGetter: (params) => params.row.isActive,
		},
		{
			field: "actions",
			type: "actions",
			flex: 1,
			sortable: false,
			align: "left",
			cellClassName: "hover-column-cell",
			headerClassName: "hover-column-header",
			renderHeader: () => <></>,
			renderCell: (params: any) => (
				<Box
					className='hover-content'
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
						gap: "8px",
						padding: "8px 12px",
					}}
				>
					{params.row.isActive ? (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": {
									color: "#000000",
									transform: "scale(1.2)",
								},
								cursor: "pointer",
							}}
						>
							<Tooltip title='Edit'>
								<EditIcon
									id={`edit-shift-purpose-${params.row.id}`}
									fontSize='small'
									onClick={() => handleOpenEdit(params.row)}
								/>
							</Tooltip>
						</Box>
					) : null}
					{params.row.isActive ? (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": { color: "red", transform: "scale(1.2)" },
								cursor: "pointer",
							}}
						>
							<Tooltip title='Deactivate'>
								<VisibilityOffIcon
									id={`toggle-shift-purpose-status-${params.row.id}`}
									fontSize='small'
									onClick={() =>
										handleDeactivate(params.row.id, params.row.name)
									}
								/>
							</Tooltip>
						</Box>
					) : (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": {
									color: "#000000",
									transform: "scale(1.2)",
								},
								cursor: "pointer",
							}}
						>
							<Tooltip title='Activate'>
								<VisibilityIcon
									id={`toggle-shift-purpose-status-${params.row.id}`}
									fontSize='small'
									onClick={() =>
										handleActivate(params.row.id, params.row.name)
									}
								/>
							</Tooltip>
						</Box>
					)}
				</Box>
			),
		},
	]

	return { columns }
}

export default useShiftPurposeColumns
